<template>
  <TableScopeNotice>
    Cette page montre <strong>ce que la plateforme SuperPDP connaît</strong> de votre e-reporting : les transactions et les paiements qu'elle a enregistrés,
    les déclarations qu'elle a envoyées à l'administration et l'aperçu de la prochaine. <strong>Rien n'est calculé ici et consulter cette page ne transmet rien.</strong>
    Les ventes aux entreprises françaises relèvent de la facturation électronique et ne sont pas concernées.
  </TableScopeNotice>

  <div class="bento-item results-tile">
    <CustomLoader ref="loader"/>

    <p v-if="error" class="state-message state-error">Les données de la plateforme n'ont pas pu être chargées. Vérifiez la connexion, puis réessayez avec « Actualiser ».</p>

    <template v-if="overview">
      <div v-for="notice in overview.notices" :key="notice.code + notice.message" class="notice" :class="'notice-' + notice.severity">{{ notice.message }}</div>

      <div class="company">
        <dl class="company-facts">
          <div v-if="overview.company?.name" class="company-fact company-name"><dt>Entreprise</dt><dd :title="overview.company.name">{{ overview.company.name }}</dd></div>
          <div v-if="overview.company?.vatRegime" class="company-fact"><dt>Régime de TVA</dt><dd>{{ vatRegimeLabel }}</dd></div>
          <div v-if="overview.company && overview.company.hasVatOnDebits !== undefined" class="company-fact"><dt>TVA sur les débits</dt><dd>{{ overview.company.hasVatOnDebits ? 'Oui' : 'Non' }}</dd></div>
          <div v-if="overview.company?.environment" class="company-fact"><dt>Environnement</dt><dd>{{ environmentLabel }}</dd></div>
        </dl>
        <button type="button" class="chip" @click="load">Actualiser</button>
      </div>

      <div class="kpis">
        <button type="button" class="kpi" :class="{selected: tab === 'operations' && filters.state === 'pending'}" @click="showOperations({state: 'pending'})"
                title="Opérations enregistrées par la plateforme et pas encore dans une déclaration envoyée">
          <span class="kpi-count">{{ overview.summary.pending }}</span>
          <span class="kpi-label">opération{{ overview.summary.pending > 1 ? 's' : '' }} à déclarer</span>
          <span class="kpi-detail">Pas encore dans une déclaration envoyée</span>
        </button>
        <button type="button" class="kpi" :class="{selected: tab === 'operations' && filters.state === 'declared'}" @click="showOperations({state: 'declared'})"
                title="Opérations rattachées par la plateforme à une déclaration envoyée">
          <span class="kpi-count">{{ overview.summary.declared }}</span>
          <span class="kpi-label">opération{{ overview.summary.declared > 1 ? 's' : '' }} déclarée{{ overview.summary.declared > 1 ? 's' : '' }}</span>
          <span class="kpi-detail">Rattachées à une déclaration envoyée</span>
        </button>
        <button type="button" class="kpi" :class="{selected: tab === 'declarations', error: overview.summary.declarationsInError > 0}" @click="tab = 'declarations'"
                title="Déclarations envoyées à l'administration par la plateforme">
          <span class="kpi-count">{{ overview.summary.declarations }}</span>
          <span class="kpi-label">déclaration{{ overview.summary.declarations > 1 ? 's' : '' }} envoyée{{ overview.summary.declarations > 1 ? 's' : '' }}</span>
          <span class="kpi-detail">{{ overview.summary.declarationsInError > 0 ? overview.summary.declarationsInError + ' rejetée(s) ou en erreur' : 'Aucune rejetée ni en erreur' }}</span>
        </button>
      </div>

      <div class="tabs" role="tablist">
        <button type="button" role="tab" class="tab" :class="{selected: tab === 'upcoming'}" :aria-selected="tab === 'upcoming'" @click="tab = 'upcoming'">
          Prochaine déclaration
        </button>
        <button type="button" role="tab" class="tab" :class="{selected: tab === 'declarations'}" :aria-selected="tab === 'declarations'" @click="tab = 'declarations'">
          Déclarations envoyées <span class="tab-count">{{ overview.declarations.length }}</span>
        </button>
        <button type="button" role="tab" class="tab" :class="{selected: tab === 'operations'}" :aria-selected="tab === 'operations'" @click="tab = 'operations'">
          Opérations <span class="tab-count">{{ overview.operations.length }}</span>
        </button>
      </div>

      <!-- Aperçu calculé par la plateforme -->
      <template v-if="tab === 'upcoming'">
        <p class="section-hint">
          Aperçu fourni par la plateforme : les données de la période en cours qu'elle n'a pas encore envoyées à l'administration.
          Tant que la période n'est pas terminée, l'aperçu est partiel.
        </p>
        <section v-for="item in overview.upcoming" :key="item.flow" class="upcoming">
          <h3 class="section-title">
            {{ E_REPORTING_FLOW_PLURAL_LABELS[item.flow] }}
            <span v-if="item.report && item.report.startPeriod" class="section-period">{{ formatPeriod(item.report.startPeriod, item.report.endPeriod) }}</span>
          </h3>
          <p v-if="item.error" class="state-message state-error">L'aperçu n'a pas pu être lu : {{ item.error }}</p>
          <p v-else-if="item.empty" class="state-message">
            Rien à déclarer : la plateforme n'a {{ item.flow === 'transaction' ? 'aucune transaction' : 'aucun paiement' }} en attente pour la période en cours.
          </p>
          <EReportingReportView v-else :report="item.report"/>
        </section>
      </template>

      <!-- Déclarations déjà envoyées à l'administration -->
      <template v-else-if="tab === 'declarations'">
        <p v-if="overview.declarations.length === 0" class="state-message">
          La plateforme n'a encore envoyé aucune déclaration à l'administration. Elle les envoie selon un calendrier qui dépend de votre régime de TVA.
        </p>
        <table v-else class="ereporting-table">
          <thead>
            <tr>
              <th class="col-period">Période</th>
              <th class="col-flow">Flux</th>
              <th class="col-status">État</th>
              <th class="col-date">Dernier événement</th>
              <th class="col-count" title="Opérations que la plateforme rattache à cette déclaration">Opérations rattachées</th>
              <th class="col-chevron"></th>
            </tr>
          </thead>
          <tbody v-for="declaration in overview.declarations" :key="declaration.id">
            <tr class="row-main" :class="{open: expanded['declaration:' + declaration.id]}" @click="toggleDeclaration(declaration)">
              <td class="col-period">{{ formatPeriod(declaration.startPeriod, declaration.endPeriod) }}</td>
              <td class="col-flow">{{ E_REPORTING_FLOW_PLURAL_LABELS[declaration.flow] }}</td>
              <td class="col-status"><span class="badge" :style="badgeStyle(declarationStatus(declaration.status))">{{ declarationStatus(declaration.status).label }}</span></td>
              <td class="col-date">{{ formatDateTime(declaration.statusDate) }}</td>
              <td class="col-count">{{ declaration.operationCount }}</td>
              <td class="col-chevron"><span class="chevron" :class="{open: expanded['declaration:' + declaration.id]}">&rsaquo;</span></td>
            </tr>
            <tr v-if="expanded['declaration:' + declaration.id]" class="row-detail">
              <td colspan="6">
                <p class="detail-title">Historique des statuts · déclaration n° {{ declaration.id }}</p>
                <ul class="event-list">
                  <li v-for="event in declaration.events" :key="event.id">
                    <span class="badge" :style="badgeStyle(declarationStatus(event.status))" :title="event.statusCode">{{ declarationStatus(event.status).label }}</span>
                    <span class="event-date">{{ formatDateTime(event.createdAt) }}</span>
                  </li>
                </ul>
                <p class="detail-title">Contenu envoyé à l'administration</p>
                <p v-if="details[declaration.id]?.loading" class="state-message">Chargement du contenu…</p>
                <p v-else-if="details[declaration.id]?.error" class="state-message state-error">Le contenu de cette déclaration n'a pas pu être lu.</p>
                <EReportingReportView v-else-if="details[declaration.id]?.report" :report="details[declaration.id].report"/>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- Opérations : chaque transaction ou paiement enregistré par la plateforme -->
      <template v-else>
        <div class="filters">
          <div class="filter-group" role="group" aria-label="État">
            <button v-for="option in stateOptions" :key="option.value" type="button" class="chip" :class="{selected: filters.state === option.value}" @click="filters.state = option.value">
              {{ option.label }} <span class="tab-count">{{ countFor('state', option.value) }}</span>
            </button>
          </div>
          <div class="filter-group" role="group" aria-label="Flux">
            <button v-for="option in flowOptions" :key="option.value" type="button" class="chip" :class="{selected: filters.flow === option.value}" @click="filters.flow = option.value">
              {{ option.label }} <span class="tab-count">{{ countFor('flow', option.value) }}</span>
            </button>
          </div>
          <div class="filter-group" role="group" aria-label="Périmètre">
            <button v-for="option in scopeOptions" :key="option.value" type="button" class="chip" :class="{selected: filters.scope === option.value}" @click="filters.scope = option.value">
              {{ option.label }} <span class="tab-count">{{ countFor('scope', option.value) }}</span>
            </button>
          </div>
        </div>

        <p v-if="filteredOperations.length === 0" class="state-message">Aucune opération ne correspond à ces filtres.</p>
        <table v-else class="ereporting-table">
          <thead>
            <tr>
              <th class="col-date">Date</th>
              <th class="col-document">Opération</th>
              <th class="col-truncate">Client</th>
              <th class="col-scope">Périmètre</th>
              <th class="col-amount col-secondary">HT</th>
              <th class="col-amount col-secondary">TVA</th>
              <th class="col-amount">TTC</th>
              <th class="col-status">État</th>
              <th class="col-chevron"></th>
            </tr>
          </thead>
          <tbody v-for="operation in visibleOperations" :key="operation.key">
            <tr class="row-main" :class="{open: expanded[operation.key]}" @click="toggle(operation.key)">
              <td class="col-date">{{ formatDay(operation.date) }}</td>
              <td class="col-document">
                <span class="cell-main">{{ documentLabel(operation) }}</span>
                <span class="cell-sub">{{ E_REPORTING_FLOW_LABELS[operation.flow] }}</span>
              </td>
              <td class="col-truncate" :title="clientLabel(operation)">{{ clientLabel(operation) }}</td>
              <td class="col-scope">
                <span class="cell-main">{{ E_REPORTING_SCOPE_LABELS[operation.scope] }}</span>
                <span v-if="operation.category" class="cell-sub">{{ categoryLabel(operation.category) }}</span>
              </td>
              <td class="col-amount col-secondary">{{ formatKnownEuro(operation.taxExclusive) }}</td>
              <td class="col-amount col-secondary">{{ formatKnownEuro(operation.taxTotal) }}</td>
              <td class="col-amount">{{ formatKnownEuro(operation.grossAmount) }}</td>
              <td class="col-status">
                <span class="badge" :style="badgeStyle(E_REPORTING_OPERATION_STATE[operation.state])">{{ E_REPORTING_OPERATION_STATE[operation.state].label }}</span>
              </td>
              <td class="col-chevron"><span class="chevron" :class="{open: expanded[operation.key]}">&rsaquo;</span></td>
            </tr>
            <tr v-if="expanded[operation.key]" class="row-detail">
              <td colspan="9">
                <div class="detail-grid">
                  <div>
                    <p class="detail-title">Ventilation par taux de TVA</p>
                    <table class="mini-table">
                      <thead><tr><th>Taux</th><th>Base HT</th><th>TVA</th><th>TTC</th></tr></thead>
                      <tbody>
                        <tr v-for="line in operation.lines" :key="line.taxPercent">
                          <td>{{ formatRate(line.taxPercent) }}</td>
                          <td>{{ formatKnownEuro(line.taxableAmount) }}</td>
                          <td>{{ formatKnownEuro(line.taxAmount) }}</td>
                          <td>{{ formatKnownEuro(lineGross(line)) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p v-if="operation.flow === 'payment'" class="detail-note">La plateforme déclare un paiement par taux de TVA, en montant toutes taxes comprises uniquement.</p>
                  </div>
                  <div>
                    <p class="detail-title">Informations de la plateforme</p>
                    <dl class="facts">
                      <template v-if="operation.clientName"><dt>Client</dt><dd :title="operation.clientName">{{ operation.clientName }}</dd></template>
                      <template v-if="operation.clientIdentifier"><dt>Identifiant</dt><dd :title="operation.clientIdentifier">{{ operation.clientIdentifier }}</dd></template>
                      <template v-if="operation.clientCountry"><dt>Pays</dt><dd>{{ operation.clientCountry }}</dd></template>
                      <template v-if="operation.issueDate"><dt>Facture émise le</dt><dd>{{ formatDay(operation.issueDate) }}</dd></template>
                      <template v-if="operation.invoiceId"><dt>Facture plateforme</dt><dd>n° {{ operation.invoiceId }}</dd></template>
                      <template v-if="operation.eventId"><dt>Événement « Encaissée »</dt><dd>n° {{ operation.eventId }}</dd></template>
                      <template v-if="operation.declarationId">
                        <dt>Déclaration</dt>
                        <dd>n° {{ operation.declarationId }}<template v-if="operation.declarationStatus"> · {{ declarationStatus(operation.declarationStatus).label }}</template></dd>
                      </template>
                      <template v-if="operation.platformStatus"><dt>Statut</dt><dd>{{ operation.platformStatus }}</dd></template>
                      <template v-if="!operation.associationId"><dt>Document local</dt><dd>Non retrouvé dans les documents transmis</dd></template>
                      <dt>Monnaie</dt><dd>{{ operation.currency }}</dd>
                    </dl>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="hiddenCount > 0" class="more">
          <button type="button" class="chip" @click="visibleCount += PAGE_SIZE">Afficher plus ({{ hiddenCount }} restante{{ hiddenCount > 1 ? 's' : '' }})</button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import CustomLoader from "@/components/CustomLoader.vue";
import TableScopeNotice from "@/components/TableScopeNotice.vue";
import EReportingReportView from "@/invoice-transmission/components/EReportingReportView.vue";
import {EReportingController} from "@/invoice-transmission/controllers/invoice_transmission/EReportingController";
import {
  categoryLabel,
  declarationStatus,
  documentLabel,
  E_REPORTING_FLOW_LABELS,
  E_REPORTING_FLOW_PLURAL_LABELS,
  E_REPORTING_OPERATION_STATE,
  E_REPORTING_SCOPE_LABELS,
  E_REPORTING_VAT_REGIME_LABELS,
  formatDateTime,
  formatDay,
  formatKnownEuro,
  formatPeriod,
  formatRate
} from "@/invoice-transmission/eReportingUtils";

const PAGE_SIZE = 50;

const ENVIRONMENT_LABELS = {sandbox: 'Bac à sable', production: 'Production'};

/**
 * E-reporting tel que la plateforme le connaît : aperçu de la prochaine déclaration, déclarations envoyées à l'administration et chaque
 * transaction ou paiement enregistré (GET uniquement : rien n'est calculé ici, rien n'est transmis).
 */
export default {
  name: 'EReportingPreview',
  components: {CustomLoader, TableScopeNotice, EReportingReportView},
  data() {
    return {
      overview: undefined,
      error: false,
      tab: 'upcoming',
      filters: {state: 'all', flow: 'all', scope: 'all'},
      expanded: {},
      // Contenu des déclarations envoyées, lu à la demande : {[id]: {loading, error, report}}
      details: {},
      visibleCount: PAGE_SIZE,
      PAGE_SIZE,
      stateOptions: [{value: 'all', label: 'Toutes'}, {value: 'pending', label: 'À déclarer'}, {value: 'declared', label: 'Déclarées'}],
      flowOptions: [{value: 'all', label: 'Tous flux'}, {value: 'transaction', label: 'Transactions'}, {value: 'payment', label: 'Paiements'}],
      scopeOptions: [{value: 'all', label: 'Tous périmètres'}, {value: 'b2c', label: 'Particuliers'}, {value: 'international', label: 'International'}],
      E_REPORTING_FLOW_LABELS, E_REPORTING_FLOW_PLURAL_LABELS, E_REPORTING_SCOPE_LABELS, E_REPORTING_OPERATION_STATE
    };
  },
  computed: {
    vatRegimeLabel() {
      const regime = this.overview?.company?.vatRegime;
      return E_REPORTING_VAT_REGIME_LABELS[regime] || regime || '-';
    },
    environmentLabel() {
      const environment = this.overview?.company?.environment;
      return ENVIRONMENT_LABELS[environment] || environment || '-';
    },
    filteredOperations() {
      return (this.overview?.operations || []).filter(operation => this.matches(operation, this.filters));
    },
    visibleOperations() {
      return this.filteredOperations.slice(0, this.visibleCount);
    },
    hiddenCount() {
      return Math.max(0, this.filteredOperations.length - this.visibleCount);
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.visibleCount = PAGE_SIZE;
        this.expanded = {};
      }
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDay, formatDateTime, formatPeriod, formatRate, formatKnownEuro, documentLabel, categoryLabel, declarationStatus,
    badgeStyle(info) {
      return {color: info.color, backgroundColor: info.backgroundColor, border: `1px solid ${info.backgroundColor === '#ffffff' ? '#b9d3f5' : info.backgroundColor}`};
    },
    matches(operation, filters) {
      return (filters.state === 'all' || operation.state === filters.state)
          && (filters.flow === 'all' || operation.flow === filters.flow)
          && (filters.scope === 'all' || operation.scope === filters.scope);
    },
    // Nombre d'opérations d'un choix, compte tenu des deux autres filtres
    countFor(group, value) {
      const filters = {...this.filters, [group]: value};
      return (this.overview?.operations || []).filter(operation => this.matches(operation, filters)).length;
    },
    clientLabel(operation) {
      return operation.clientName || operation.clientIdentifier || '—';
    },
    // Un paiement ne connaît que le montant toutes taxes comprises ; une transaction a le hors taxe et la TVA
    lineGross(line) {
      if (line.grossAmount !== undefined) return line.grossAmount;
      return line.taxableAmount !== undefined && line.taxAmount !== undefined ? Math.round((line.taxableAmount + line.taxAmount) * 100) / 100 : undefined;
    },
    showOperations(filters) {
      this.filters = {state: 'all', flow: 'all', scope: 'all', ...filters};
      this.tab = 'operations';
    },
    toggle(key) {
      this.expanded = {...this.expanded, [key]: !this.expanded[key]};
    },
    async toggleDeclaration(declaration) {
      const key = 'declaration:' + declaration.id;
      this.toggle(key);
      if (this.expanded[key]) await this.loadDeclaration(declaration.id);
    },
    // Contenu d'une déclaration envoyée : lu une seule fois, relu seulement après une erreur
    async loadDeclaration(id) {
      const current = this.details[id];
      if (current?.loading || current?.report) return;
      this.details = {...this.details, [id]: {loading: true}};
      const resp = await EReportingController.getDeclaration(id);
      this.details = {...this.details, [id]: resp && !resp.errors && resp.report ? {report: resp.report} : {error: true}};
    },
    async load() {
      this.$refs.loader?.startLoading();
      const resp = await EReportingController.getOverview();
      this.$refs.loader?.stopLoading();
      if (resp && !resp.errors) {
        this.overview = resp;
        this.error = false;
        this.expanded = {};
        this.details = {};
        this.visibleCount = PAGE_SIZE;
      } else {
        this.error = true;
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

// Code couleur : bleu = sélection / information, vert = réussi, orange puis rouge = travail à faire ou en erreur, citron réservé à la position courante

.results-tile
  position relative
  min-height 120px

.state-message
  margin 12px 0 4px
  color #1F3A63
  font-weight 600

.state-error
  color #a61b1b

.section-hint
  margin 0 0 14px
  color #4A5F82
  font-size 0.9em
  line-height 1.4

.section-title
  margin 16px 0 6px
  color #1F3A63
  font-size 1.05em
  font-weight 800

.section-period
  margin-left 8px
  color #4A5F82
  font-size 0.85em
  font-weight 600

.upcoming + .upcoming
  margin-top 12px
  padding-top 4px
  border-top 1px solid #E3ECF8

.notice
  margin-bottom 12px
  padding 10px 14px
  border-radius 10px
  font-weight 600
  line-height 1.4

.notice-warning
  background #FFF0D6
  color #7a4300

.notice-error
  background #fde0e0
  color #a61b1b

.notice-info
  background #E4EEFC
  color #1F3A63

// ---- Entreprise
.company
  display flex
  align-items flex-start
  justify-content space-between
  gap 12px
  margin-bottom 16px

.company-facts
  display flex
  flex-wrap wrap
  gap 8px 28px
  min-width 0
  margin 0

.company-fact
  min-width 0
  dt
    color #4A5F82
    font-size 0.8em
    font-weight 700
  dd
    margin 0
    color #1F3A63
    font-weight 700

.company-name dd
  max-width 280px
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

// ---- Indicateurs
.kpis
  display grid
  grid-template-columns repeat(auto-fit, minmax(210px, 1fr))
  gap 10px
  margin-bottom 18px

.kpi
  display flex
  flex-direction column
  gap 2px
  padding 12px 16px
  border 1px solid #D6E3F5
  border-radius 14px
  background-image linear-gradient(160deg, #FAFCFF, #EDF3FD)
  color #1F3A63
  text-align left
  cursor pointer
  &:hover
    box-shadow 0 4px 14px rgba(47, 95, 168, 0.15)
  &.selected
    border-color #2F5FA8
    box-shadow 0 0 0 2px rgba(47, 95, 168, 0.25)
  &.error
    border-color #E8A0A0
    background-image linear-gradient(160deg, #FFFAFA, #FDE0E0)
    .kpi-count
      color #a61b1b

.kpi-count
  font-size 1.8em
  font-weight 800
  line-height 1.1

.kpi-label
  font-size 0.9em
  font-weight 700

.kpi-detail
  font-size 0.8em
  font-weight 600
  color #4A5F82

// ---- Onglets et filtres : la sélection est en bleu
.tabs
  display flex
  flex-wrap wrap
  gap 8px
  margin-bottom 14px
  border-bottom 2px solid #D6E3F5

.tab
  margin-bottom -2px
  padding 9px 16px
  border 0
  border-bottom 3px solid transparent
  background transparent
  color #1F3A63
  font-weight 700
  cursor pointer
  &.selected
    border-bottom-color #2F5FA8
    color #2F5FA8

.tab-count
  display inline-block
  min-width 20px
  margin-left 4px
  padding 0 6px
  border-radius 999px
  background #E4EEFC
  color #1F3A63
  font-size 0.8em
  font-weight 700
  text-align center

.filters
  display flex
  flex-wrap wrap
  gap 10px 24px
  margin-bottom 12px

.filter-group
  display flex
  flex-wrap wrap
  gap 8px

.chip
  padding 7px 14px
  border 1px solid #D6E3F5
  border-radius 999px
  background white
  color #1F3A63
  font-weight 600
  cursor pointer
  &:hover
    background #EDF3FD
  &.selected
    border-color #2F5FA8
    background #2F5FA8
    color white
    .tab-count
      background rgba(255, 255, 255, 0.25)
      color white

.more
  margin-top 14px
  text-align center

// ---- Tableaux : jamais plus larges que la carte, le client est tronqué avec une infobulle
.ereporting-table
  width 100%
  border-collapse collapse
  th, td
    padding 9px 8px
    text-align left
    vertical-align middle
    border-bottom 1px solid #E3ECF8
  th
    color #1F3A63
    font-size 0.85em
    font-weight 800

.row-main
  cursor pointer
  color #17233f
  &:hover, &.open
    background #F4F8FE

.col-date, .col-flow, .col-count, .col-amount, .col-status, .col-period, .col-scope, .col-document
  white-space nowrap

.col-amount, .col-count
  text-align right !important

.col-truncate
  max-width 0
  width 22%
  min-width 90px
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.col-chevron
  width 24px
  padding-left 0 !important
  padding-right 0 !important
  text-align center !important

.chevron
  display inline-block
  font-size 1.4em
  font-weight 700
  color #2F5FA8
  transition transform .15s ease
  &.open
    transform rotate(90deg)

.cell-main
  display block
  font-weight 700

.cell-sub
  display block
  font-size 0.82em
  color #4A5F82

.badge
  display inline-block
  padding 2px 10px
  border-radius 999px
  font-size 0.85em
  font-weight 700
  white-space nowrap

// ---- Détail d'une ligne
.row-detail td
  padding 14px 12px 18px
  background #F9FBFF

.detail-grid
  display grid
  grid-template-columns repeat(auto-fit, minmax(280px, 1fr))
  gap 16px 28px

.detail-title
  margin 0 0 8px
  color #1F3A63
  font-size 0.85em
  font-weight 800
  text-transform uppercase

.detail-note
  margin 8px 0 0
  color #1F3A63
  font-weight 600

.mini-table
  width 100%
  border-collapse collapse
  th, td
    padding 5px 8px
    text-align right
    border-bottom 1px solid #E3ECF8
    white-space nowrap
  th:first-child, td:first-child
    text-align left
  th
    color #4A5F82
    font-size 0.8em

.event-list
  display flex
  flex-direction column
  gap 6px
  margin 0 0 14px
  padding 0
  list-style none
  li
    display flex
    align-items center
    gap 12px

.event-date
  color #4A5F82
  font-weight 600

.facts
  display grid
  grid-template-columns max-content 1fr
  gap 4px 14px
  margin 0
  dt
    color #4A5F82
    font-weight 700
  dd
    margin 0
    min-width 0
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

// Sous 1100 px, les colonnes HT et TVA passent dans le détail de la ligne (le TTC reste visible)
@media only screen and (max-width: 1100px)
  .col-secondary
    display none
</style>
